import { getDeviceObjectTypeBy, getFullVelocityUrl, handleResponse } from '@/Helper';
import { deviceAdminApi, graphicApi } from '@/api';
import { SecuredComponents, SubPermissions, User, getPermissionErrorMessage } from '@/model/AccountModel';
import { AlarmModel } from '@/model/AlarmModel';
import {
	ButtonOption,
	DictionaryCustom,
	DigitracAvailableOptions,
	DigitracDisabledOptions,
	PortResetEncryptionActionOptions,
	SelectOptions,
} from '@/model/CommonModel';
import {
	ActionDeviceTypes,
	ActionDigitrac,
	ActionDigitracContextMenu,
	CurrentDeviceControlObj,
	DeviceObjectType,
	ObjectTypes,
	SendDeviceCommand,
} from '@/model/DeviceAdminModel';
import {
	ActionGraphic,
	ActionGraphicContextMenu,
	ActionToExecute,
	AlarmQueueTreeNode,
	BadgeStudioBarCode,
	BadgeStudioImage,
	C_ObjectType,
	C_PictureTypeCustom,
	ContextMenuObjTypes,
	ContextMenuRequest,
	E_ObjectTypes,
	GraphicAlarmPermission,
	GraphicCommandSet,
	GraphicCurrentAction,
	GraphicLink,
	GraphicPageMode,
	GraphicSortDirections,
	GraphicText,
	GraphicTreeEventDataNode,
	GraphicTreeNode,
	MonitoringPoint,
	ObjectBaseProperties,
	ObjectLibraryFolder,
	ObjectLibraryTreeNode,
	PointMonitoringZone,
	PointState,
	ProccessLiveAlarmEventParams,
	SpaceObject,
	SpecialEventType,
	ToolbarSection,
	UpdateTreeLevelNode,
	VelocityObject,
	ZoneState,
} from '@/model/GraphicModel';
import { HardwarePermission, SearchType } from '@/model/RolesModel';
import {
	BorderOutlined,
	CloudOutlined,
	DeleteOutlined,
	DeploymentUnitOutlined,
	ExclamationCircleOutlined,
	ExpandAltOutlined,
	FolderOpenOutlined,
	FolderOutlined,
	FontSizeOutlined,
	FullscreenOutlined,
	HddOutlined,
	InsertRowLeftOutlined,
	MobileOutlined,
	RollbackOutlined,
	RotateRightOutlined,
	StockOutlined,
	TabletTwoTone,
	ZoomInOutlined,
	ZoomOutOutlined,
} from '@ant-design/icons';
import { Checkbox, Tooltip, Typography } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import React, { CSSProperties } from 'react';
import { ContextMenu, WarningProtocolMessage } from '../common';
import { DoorModal, ExpansionRelayModal, InputModal, PortModal, ReaderModal, XboxModal } from '../devicecontrol/DigiTrac';
import { ControllerModal } from '../devicecontrol/DigiTrac/ControllerModal/ControllerModal';
import { DeviceContent } from './AlarmQueue';
import { isSameAlarmQueueKey } from './AlarmQueue/helper';
import styles from './graphic.module.scss';

const { Paragraph } = Typography;

const user: User = getUser();
const velocityUrl = getFullVelocityUrl();
const newDrawingFileName = `${velocityUrl}/Graphic/content/drawings/dwg/NewDrawing.png?_=${getScriptVersion()}`;

export type ConfigureDeviceParams = {
	deviceInfo: CurrentDeviceControlObj;
	setShouldResetSearchColumn: (flag: boolean) => void;
	setRedirectPage: (flag: boolean) => void;
	handleCancel?: (device: CurrentDeviceControlObj) => void;
	handleSave?: () => void;
};

export const EraserIcon: React.FC<{ elementStyle: CSSProperties }> = ({ elementStyle }) => {
	return (
		<span className='anticon anticon-font-size' style={elementStyle}>
			<svg width='1em' height='1em' viewBox='0 0 24 24' fill='currentColor'>
				<path d='m2.586 15.408 4.299 4.299a.996.996 0 0 0 .707.293h12.001v-2h-6.958l7.222-7.222c.78-.779.78-2.049 0-2.828L14.906 3a2.003 2.003 0 0 0-2.828 0l-4.75 4.749-4.754 4.843a2.007 2.007 0 0 0 .012 2.816zM13.492 4.414l4.95 4.95-2.586 2.586L10.906 7l2.586-2.586zM8.749 9.156l.743-.742 4.95 4.95-4.557 4.557a1.026 1.026 0 0 0-.069.079h-1.81l-4.005-4.007 4.748-4.837z' />
			</svg>
		</span>
	);
};

export const MoveIcon: React.FC<{ elementStyle: CSSProperties }> = ({ elementStyle }) => {
	return (
		<span className='anticon anticon-font-size' style={elementStyle}>
			<svg width='1em' height='1em' viewBox='0 0 489.9 489.9' fill='currentColor'>
				<path d='M406.2,173.55c-4.8,4.8-4.8,12.5,0,17.3l41.8,41.8H333.3v-63.9c0-6.8-5.5-12.3-12.3-12.3h-63.9V41.85l41.8,41.8 c2.4,2.4,5.5,3.6,8.7,3.6s6.3-1.2,8.7-3.6c4.8-4.8,4.8-12.5,0-17.3l-62.7-62.7c-2.3-2.3-5.4-3.6-8.7-3.6s-6.4,1.3-8.7,3.6 l-62.7,62.7c-4.8,4.8-4.8,12.5,0,17.3s12.5,4.8,17.3,0l41.8-41.8v114.7h-63.9c-6.8,0-12.3,5.5-12.3,12.3v63.9H41.8l41.8-41.8 c4.8-4.8,4.8-12.5,0-17.3s-12.5-4.8-17.3,0l-62.7,62.7c-4.8,4.8-4.8,12.5,0,17.3l62.7,62.7c2.4,2.4,5.5,3.6,8.7,3.6 s6.3-1.2,8.7-3.6c4.8-4.8,4.8-12.5,0-17.3l-41.8-41.8h114.7v63.9c0,6.8,5.5,12.3,12.3,12.3h63.9v114.7l-41.9-41.9 c-4.8-4.8-12.5-4.8-17.3,0s-4.8,12.5,0,17.3l62.7,62.7c2.3,2.3,5.4,3.6,8.7,3.6s6.4-1.3,8.7-3.6l62.7-62.7 c4.8-4.8,4.8-12.5,0-17.3s-12.5-4.8-17.3,0l-41.8,41.8v-114.7h63.9c6.8,0,12.3-5.5,12.3-12.3v-63.9h114.7l-42,41.8 c-4.8,4.8-4.8,12.5,0,17.3c2.4,2.4,5.5,3.6,8.7,3.6s6.3-1.2,8.7-3.6l62.7-62.7c2.3-2.3,3.6-5.4,3.6-8.7s-1.3-6.4-3.6-8.7 l-62.7-62.7C418.7,168.75,411,168.75,406.2,173.55z M308.8,308.85H181v-127.8h127.8L308.8,308.85L308.8,308.85z' />
			</svg>
		</span>
	);
};

export const EllipseIcon: React.FC<{ elementStyle: CSSProperties }> = ({ elementStyle }) => {
	return (
		<span className='anticon anticon-font-size' style={elementStyle}>
			<svg width='1em' height='1em' viewBox='0 0 88.332 88.333' fill='currentColor'>
				<path d='M44.166,75.062C19.812,75.062,0,61.202,0,44.167C0,27.13,19.812,13.27,44.166,13.27c24.354,0,44.166,13.859,44.166,30.896 C88.332,61.204,68.52,75.062,44.166,75.062z M44.166,16.27C21.467,16.27,3,28.784,3,44.167c0,15.381,18.467,27.896,41.166,27.896 s41.166-12.515,41.166-27.896C85.332,28.785,66.865,16.27,44.166,16.27z' />
			</svg>
		</span>
	);
};

export const Polyline: React.FC<{ elementStyle: CSSProperties }> = ({ elementStyle }) => {
	return (
		<span className='anticon anticon-font-size' style={elementStyle}>
			<svg width='1em' height='1em' viewBox='0 0 24 24' fill='currentColor'>
				<path d='M2 4.8V22h20V2l-11.538 8.4zm19-.835V21H3V6.661l7.489 4.956z' />
				<path fill='none' d='M0 0h24v24H0z' />
			</svg>
		</span>
	);
};

export const handleOnOpenDefaultDrawing = () => {};

export const getToolbarItems = (section: ToolbarSection): ButtonOption[] => {
	const redColorStyle: CSSProperties = { color: 'red' };
	const blackColorStyle: CSSProperties = { color: 'black' };
	const spaceObjectColorStyle: CSSProperties = { color: '#406080' };

	switch (section) {
		case ToolbarSection.Drawing:
			return [
				{
					id: 'Rectangle',
					label: _('Rectangle'),
					icon: <BorderOutlined style={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'FilledRectangle',
					label: _('FilledRectangle'),
					icon: <TabletTwoTone twoToneColor={'red'} />,
					onClick: () => {},
				},
				{
					id: 'Cloud',
					label: _('Cloud'),
					icon: <CloudOutlined style={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'Ellipse',
					label: _('Ellipse'),
					icon: <StockOutlined style={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'Polygon',
					label: _('Polygon'),
					icon: <Polyline elementStyle={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'Arrow',
					label: _('Arrow'),
					icon: <RollbackOutlined style={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'Text',
					label: _('Text'),
					icon: <FontSizeOutlined style={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'DeleteLine',
					label: _('DeleteLine'),
					icon: <EraserIcon elementStyle={redColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'ClearLines',
					label: _('ClearLines'),
					icon: <DeleteOutlined style={redColorStyle} />,
					separator: true,
					onClick: () => {},
				},
			];
		case ToolbarSection.SpaceObject:
			return [
				{
					id: 'RectangleObject',
					label: _('RectangleObject'),
					icon: <BorderOutlined style={spaceObjectColorStyle} />,
					onClick: () => {
						const cStyles = {
							fill: '#d8e1e3',
							'fill-opacity': '0.1',
							stroke: '#CCC',
							'stroke-width': '0.5',
							'stroke-opacity': '0.7',
							'stroke-linejoin': 'round',
						};
						const now = new Date().getTime();
					},
				},
				{
					id: 'RotateObject',
					label: _('RotateObject'),
					icon: <RotateRightOutlined style={spaceObjectColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'MoveObject',
					label: _('MoveObject'),
					icon: <MoveIcon elementStyle={spaceObjectColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'ResizeObject',
					label: _('ResizeObject'),
					icon: <ExpandAltOutlined style={spaceObjectColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'DeleteObject',
					label: _('DeleteObject'),
					icon: <EraserIcon elementStyle={spaceObjectColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'ClearObjects',
					label: _('ClearObjects'),
					icon: <DeleteOutlined style={spaceObjectColorStyle} />,
					separator: true,
					onClick: () => {},
				},
			];
		default:
			return [
				{
					id: 'ZoomIn',
					label: _('ZoomIn'),
					icon: <ZoomInOutlined style={blackColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'ZoomOut',
					label: _('ZoomOut'),
					icon: <ZoomOutOutlined style={blackColorStyle} />,
					onClick: () => {},
				},
				{
					id: 'ExtendZoom',
					label: _('ExtendZoom'),
					icon: <FullscreenOutlined style={blackColorStyle} />,
					onClick: () => {},
				},
			];
	}
};

type GraphicDeviceRenderNode = {
	isObjectDirectory: boolean;
	searchValue: string;
	currentEditionAction?: GraphicCurrentAction;
	renameFieldId?: string;
	onClickContextMenu: (node: ObjectLibraryTreeNode) => void;
	onCancelRename?: () => void;
	onSaveRename?: (node: ObjectLibraryTreeNode) => void;
	treeData: ObjectLibraryTreeNode[];
	expandedKeys: React.Key[];
	onChangeDeviceCheckbox: (event: CheckboxChangeEvent, node: ObjectLibraryTreeNode) => void;
};

export const keywordFilter = (parentNode: ObjectLibraryTreeNode, keyword: string) => {
	keyword = keyword?.trim()?.toLowerCase() ?? '';
	let newNodes: React.Key[] = [];
	for (const n of parentNode.children) {
		let childrenNodes: React.Key[] = [];
		if (n.children.length) {
			childrenNodes = keywordFilter(n, keyword);
		}

		if (childrenNodes.length || (n.folderType === ObjectLibraryFolder.ITEM && n.displayName && n.displayName.toLowerCase().indexOf(keyword) > -1)) {
			childrenNodes.push(n.key);
		}

		newNodes = newNodes.concat(childrenNodes);
	}

	if (parentNode.folderType === ObjectLibraryFolder.ITEM && parentNode.displayName && parentNode.displayName.toLowerCase().indexOf(keyword) > -1) {
		newNodes.push(parentNode.key);
	}

	return newNodes;
};

const highlightStyle: React.CSSProperties = { color: '#1890ff', fontWeight: 'bold' };
const tooltipStyles: CSSProperties = { maxWidth: '450px', fontSize: '12px' };
export const mapDeviceRenderNode = ({
	isObjectDirectory,
	treeData,
	searchValue,
	expandedKeys,
	onClickContextMenu,
	onCancelRename,
	onSaveRename,
	onChangeDeviceCheckbox,
}: GraphicDeviceRenderNode): GraphicTreeNode[] => {
	if (!treeData) {
		return treeData;
	}

	const searchText = searchValue?.trim()?.toLowerCase() ?? '';
	return treeData.map((item, dataIndex) => {
		let title: React.ReactNode;

		const tooltipTitle =
			item.folderType === ObjectLibraryFolder.ITEM ? (
				<div className={styles.tooltipTitle}>
					<div>
						<span>{item.displayName}</span>
					</div>
					<div>
						<span>{item.extendedData.address}</span>
					</div>
				</div>
			) : (
				''
			);

		let index: number = -1;
		if (item.folderType === ObjectLibraryFolder.ITEM) {
			index = item.displayName ? item.displayName.toLowerCase().indexOf(searchText) : -1;
		}

		let beforeStr: string = item.displayName;
		let matchStr: string = '';
		let afterStr: string = '';
		if (index >= 0) {
			beforeStr = item.displayName.substr(0, index);
			matchStr = item.displayName.substr(index, searchText.length);
			afterStr = item.displayName.substr(index + searchText.length);
		}

		const highlightedName: JSX.Element = (
			<span id={`name-${item.key}-${dataIndex}`}>
				{beforeStr}
				<span style={highlightStyle}>{matchStr}</span>
				{afterStr}
			</span>
		);

		const isFolder = item.folderType === ObjectLibraryFolder.FOLDER;
		const isItemExpanded = expandedKeys.includes(item.key);
		const isIndeterminate = item.isChecked && !item.allChildrenSelected;

		title = item.options ? (
			<ContextMenu options={item.options} onClickOption={() => onClickContextMenu(item)}>
				<span>
					<Tooltip trigger={['hover', 'click']} placement='right' title={tooltipTitle} overlayStyle={tooltipStyles}>
						<Paragraph ellipsis={{ rows: 1 }}>{highlightedName}</Paragraph>
					</Tooltip>
				</span>
			</ContextMenu>
		) : (
			<>
				<Tooltip trigger={['hover', 'click']} placement='right' title={tooltipTitle} overlayStyle={tooltipStyles}>
					<span>
						<Paragraph ellipsis={{ rows: 1 }}>{highlightedName}</Paragraph>
					</span>
				</Tooltip>
			</>
		);

		const isServer = item.extendedData.objectType === ObjectTypes.servers;
		const nodeIcon = getObjectLibraryNodeIcon(item.extendedData.objectType, isFolder, isItemExpanded) || (item.icon as React.ReactNode);
		const customIconCheckbox = (
			<div className={cx({ [styles.withoutCheckbox]: isServer, [styles.withCheckbox]: !isServer })}>
				{isObjectDirectory && !isServer && (
					<Checkbox
						id={`${dataIndex}-checkboxTree`}
						key={`${item.key}-${item.isChecked}`}
						indeterminate={isIndeterminate}
						checked={item.isChecked}
						onChange={e => onChangeDeviceCheckbox(e, item)}
					/>
				)}
				{nodeIcon}
			</div>
		);

		return {
			...item,
			title,
			icon: customIconCheckbox,
			children: mapDeviceRenderNode({
				isObjectDirectory,
				treeData: item.children,
				searchValue,
				expandedKeys,
				onClickContextMenu,
				onCancelRename,
				onSaveRename,
				onChangeDeviceCheckbox,
			}),
		};
	});
};

export const getObjectLibraryNodeIcon = (objectType: ObjectTypes, isFolder: boolean, isExpanded?: boolean): React.ReactNode => {
	if (isFolder) {
		return isExpanded ? <FolderOpenOutlined /> : <FolderOutlined />;
	}

	switch (objectType) {
		case ObjectTypes.pollingPorts:
			return <DeploymentUnitOutlined />;
		case ObjectTypes.xboxes:
			return <HddOutlined />;
		case ObjectTypes.doors:
		case ObjectTypes.readers:
		case ObjectTypes.inputs:
		case ObjectTypes.relays:
		case ObjectTypes.expansionInputs:
		case ObjectTypes.expansionRelays:
			return <MobileOutlined />;
		case ObjectTypes.hardwareControllers:
			return <InsertRowLeftOutlined />;
	}

	return <ExclamationCircleOutlined />;
};

export const createOptionsGraphicDirectory = (): SelectOptions<ActionToExecute>[] => {
	return [
		{
			id: 'graphicContextMenuDeleteButton',
			label: _('Delete'),
			separator: true,
			title: getPermissionErrorMessage(true),
			disabled: false,
			value: ActionToExecute.Delete,
		},
		{
			id: 'graphicContextMenuDesignButton',
			label: _('Design'),
			separator: true,
			title: getPermissionErrorMessage(true),
			disabled: false,
			value: ActionToExecute.Design,
		},
		{
			id: 'graphicContextMenuRenameButton',
			label: _('Rename'),
			separator: true,
			title: getPermissionErrorMessage(true),
			disabled: false,
			value: ActionToExecute.Rename,
		},
	];
};

const validObjectTypes = (objectType: ObjectTypes) => {
	return (
		objectType === ObjectTypes.pollingPorts ||
		objectType === ObjectTypes.xboxes ||
		objectType === ObjectTypes.hardwareControllers ||
		objectType === ObjectTypes.doors ||
		objectType === ObjectTypes.expansionInputs ||
		objectType === ObjectTypes.expansionRelays ||
		objectType === ObjectTypes.inputs ||
		objectType === ObjectTypes.relays ||
		objectType === ObjectTypes.readers ||
		objectType === ObjectTypes.cctvCameras
	);
};

export const createOptionsObjectLibrary = (node: ObjectLibraryTreeNode): SelectOptions<ActionToExecute>[] => {
	const {
		extendedData: { actionToExecute, objectType },
		isContextMenuValid,
		folderType,
	} = node;

	if (validObjectTypes(objectType) && actionToExecute === ActionToExecute.ConfigureDevice) {
		return [
			{
				id: 'graphicContextMenuConfigureDeviceButton',
				key: node.key,
				label: _('ConfigureDevice'),
				separator: false,
				title: getPermissionErrorMessage(true),
				disabled: false,
				value: ActionToExecute.ConfigureDevice,
			},
		];
	}

	if (objectType === ObjectTypes.none || objectType === ObjectTypes.servers || !isContextMenuValid || actionToExecute === ActionToExecute.None) {
		return [];
	}

	if (folderType === ObjectLibraryFolder.ITEM && validObjectTypes(objectType)) {
		return [
			{
				id: 'graphicContextMenuConfigureDeviceButton',
				label: _('ConfigureDevice'),
				separator: false,
				title: getPermissionErrorMessage(true),
				disabled: false,
				value: ActionToExecute.ConfigureDevice,
			},
		];
	}

	const newOptions = (isFolder: boolean) => {
		return [
			{
				id: 'graphicContextMenuNewButton',
				label: _('New'),
				separator: true,
				title: getPermissionErrorMessage(true),
				disabled: !isFolder,
				value: ActionToExecute.New,
			},
			{
				id: 'graphicContextMenuImportFromFileButton',
				label: _('ImportFromFile'),
				separator: true,
				title: getPermissionErrorMessage(true),
				disabled: !isFolder,
				value: ActionToExecute.ImportFromFile,
			},
		];
	};

	const renameOption = {
		id: 'graphicContextMenuRenameButton',
		label: _('Rename'),
		separator: true,
		title: getPermissionErrorMessage(true),
		disabled: false,
		value: ActionToExecute.Rename,
	};

	let saveOptions = (isFolder: boolean) => {
		return [
			{
				id: 'graphicContextMenuSaveButton',
				label: _('Save'),
				separator: true,
				title: getPermissionErrorMessage(true),
				disabled: false,
				value: ActionToExecute.Save,
			},
			{
				id: 'graphicContextMenuExportToFileButton',
				label: _('ExportToFile'),
				separator: true,
				title: getPermissionErrorMessage(true),
				disabled: isFolder,
				value: ActionToExecute.ExportToFile,
			},
			{
				id: 'graphicContextMenuDuplicateButton',
				label: _('Duplicate'),
				separator: true,
				title: getPermissionErrorMessage(true),
				disabled: isFolder,
				value: ActionToExecute.Duplicate,
			},
		];
	};

	const deleteOption = {
		id: 'graphicContextMenuDeleteButton',
		label: _('Delete'),
		separator: true,
		title: getPermissionErrorMessage(true),
		disabled: false,
		value: ActionToExecute.Delete,
	};

	/*TODO FIX THIS
	if (folderType === ObjectLibraryFolder.SPECIAL) {
		return [
			{
				id: 'graphicContextMenuCustomLinksButton',
				label: _('CustomLinks'),
				separator: false,
				title: getPermissionErrorMessage(true),
				disabled: false,
				value: ActionToExecute.CustomLink,
			},
		];
	}*/

	if (folderType === ObjectLibraryFolder.FOLDER && !validObjectTypes(objectType)) {
		//return [...newOptions(true), renameOption, ...saveOptions(true), deleteOption];
		return [renameOption, deleteOption];
	}

	if (folderType === ObjectLibraryFolder.ITEM && !validObjectTypes(objectType)) {
		//return [renameOption, ...saveOptions(false), deleteOption];
		return [renameOption, deleteOption];
	}

	/*
	if (folderType === ObjectLibraryFolder.TOPOLOGY) {
		return [
			{
				id: 'graphicContextMenuRefreshButton',
				label: _('Refresh'),
				separator: false,
				title: getPermissionErrorMessage(true),
				disabled: false,
				value: ActionToExecute.Refresh,
			},
		];
	}
*/
};

export const createUpdateTreeNode = (node: GraphicTreeEventDataNode, children: UpdateTreeLevelNode[]): UpdateTreeLevelNode => {
	const { displayName, key, parentId, treeLevel } = node;
	return { displayName, canvasId: Number(key), parentId, treeLevel, children };
};

export const generateUpdateTreeNode = (children: GraphicTreeEventDataNode[]): UpdateTreeLevelNode[] => {
	const newTreeNode: UpdateTreeLevelNode[] = children.map(child => {
		let updateTreeNode: UpdateTreeLevelNode[] = [];
		if (child.children.length) {
			updateTreeNode = generateUpdateTreeNode(child.children);
		}

		return createUpdateTreeNode(child, updateTreeNode);
	});

	return newTreeNode;
};

export const getConfigureDeviceModal = (params: ConfigureDeviceParams): JSX.Element => {
	const { deviceInfo, handleCancel, handleSave, setRedirectPage, setShouldResetSearchColumn } = params;

	switch (deviceInfo.DeviceObjectType) {
		case DeviceObjectType.Input:
		case DeviceObjectType.ExpansionInputs:
			return (
				<InputModal
					currentDeviceObj={deviceInfo}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Relay:
		case DeviceObjectType.ExpansionRelays:
			return (
				<ExpansionRelayModal
					currentDeviceObj={deviceInfo}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Reader:
			return (
				<ReaderModal
					address={deviceInfo.Address}
					id={deviceInfo.Id}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Door:
			return (
				<DoorModal
					currentDevice={deviceInfo}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Xbox:
			return (
				<XboxModal
					xboxToBeEditable={deviceInfo}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Port:
			return (
				<PortModal
					address={deviceInfo.Address}
					id={deviceInfo.Id}
					setShouldResetSearchColumn={() => setShouldResetSearchColumn(true)}
					setRedirectPage={() => setRedirectPage(true)}
					cancelHandle={handleCancel}
					saveCallBack={handleSave}
				/>
			);
		case DeviceObjectType.Controller:
			const params = new URLSearchParams(location.search);
			params.set('controllerId', deviceInfo.Id.toString());
			window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);

			return (
				<ControllerModal
					currentDevice={deviceInfo}
					isModal={true}
					navigationChildTitle={_('Controllers')}
					navigationParentTitle={_('DeviceControl')}
					saveCallBack={handleSave}
					cancelHandle={handleCancel}
					callbackRedirect={() => setRedirectPage(true)}
				/>
			);
		default:
			return <></>;
	}
};

export const getSpaceObject = (spaceObjectId: string): SpaceObject => {
	const spaceObject = null;
	return spaceObject;
};

export const getContextMenuRequest = (spaceObjectId: string, isDesignMode: boolean): ContextMenuRequest => {
	const spaceObject: SpaceObject = getSpaceObject(spaceObjectId);
	const { Address, VOType: voType, ObjectKey, VOKey, PictureType, CObjectType } = spaceObject.customContent;
	const contextMenuRequest: ContextMenuRequest = {
		ContextMenuObjType: ContextMenuObjTypes.VIO,
		ObjectAddress: Address,
		VOType: voType,
		ObjectKey,
		MenuId: 1,
		VOKey,
	};
	if (isDesignMode) {
		if (CObjectType === C_ObjectType.OT_TEXT) contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.TEXT_DESIGN;
		else if (VOKey) contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.VIO_DESIGN;
	} else {
		switch (PictureType) {
			case C_PictureTypeCustom.MDOT_VIO:
				contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.VIO;
				break;
			case C_PictureTypeCustom.MDOT_PMZ:
				contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.DTII_PMZ;
				break;
			case C_PictureTypeCustom.MDOT_IDS_AREA:
				contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.IDS_AREA;
				break;
			case C_PictureTypeCustom.MDOT_CREDINFO:
				contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.CRED_INFO;
				break;
			case C_PictureTypeCustom.MDOT_ALARMINFO:
				contextMenuRequest.ContextMenuObjType = ContextMenuObjTypes.ALARM_INFO;
				break;
		}
	}
	return contextMenuRequest;
};

export const sortTreeNodesAscendent = (clonedMapList: UpdateTreeLevelNode[]): UpdateTreeLevelNode[] =>
	clonedMapList.sort((a: UpdateTreeLevelNode, b: UpdateTreeLevelNode) => {
		const firstValue: string = a.displayName;
		const secondValue: string = b.displayName;

		return firstValue.localeCompare(secondValue);
	});

export const removeSpaceObject = (spaceObjectAddress: string, deviceAddress: string, spaceObjectId: string, deleteChildren: boolean): boolean => {
	let keepElement = true;
	const spaceAddress: string = spaceObjectAddress.toUpperCase().trim();
	const eventAddress: string = deviceAddress.toUpperCase().trim();

	if (deleteChildren) {
		keepElement = !spaceAddress.includes(eventAddress);
	} else {
		keepElement = spaceAddress !== eventAddress;
	}

	if (!keepElement) {
	}

	return keepElement;
};

export const getBaseGraphicAlarmPermission = (): GraphicAlarmPermission => {
	return {
		CanAcknowledge: User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_Acknowledge).allowed,
		CanAcknowledgeMultiple: User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_AcknowledgeMultiple).allowed,
		CanClear: User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_ClearinStandardMode).allowed,
		CanClearMultiple: User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_ClearMultiple).allowed,
		CanClearRestricted: User.getSubComponentPermission(user, SubPermissions.AlarmViewer_Alarms_ClearinRestrictedMode).allowed,
		MaxAlarmLevel: user.maxAlarmLevelCanAcknowledge,
		RequireNoteMultipleAcknowledge: user.forceNoteForMultipleAcknowledge,
		RequireNoteMultipleClear: user.forceNoteForMultipleClear,
		IsClearUnacknowledgedAllowed: user.isClearUnacknowledgedAllowed,
		RequireNoteOnAcknowledge: user.requireNoteOnAcknowledge,
		RequireNoteOnClear: user.requireNoteOnClear,
	};
};

export const isValidToLoadDevices = (objectType: ObjectTypes) => {
	const validObjectTypes = [
		ObjectTypes.doors,
		ObjectTypes.inputs,
		ObjectTypes.relays,
		ObjectTypes.readers,
		ObjectTypes.expansionInputs,
		ObjectTypes.expansionRelays,
	];
	return validObjectTypes.includes(objectType);
};

export const resetCheckedObjectLibraryTree = (childNode: ObjectLibraryTreeNode) => {
	const { children } = childNode;

	childNode.isChecked = false;
	childNode.allChildrenSelected = false;
	for (let i = 0; i < children.length; i++) {
		resetCheckedObjectLibraryTree(children[i]);
	}
};

export const buildObjectLibraryTree = (
	childNode: ObjectLibraryTreeNode,
	parentNode: ObjectLibraryTreeNode = undefined,
	controllerFullSelected: HardwarePermission[] = undefined
) => {
	const { children } = childNode;

	childNode.parentNode = parentNode;
	childNode.options = createOptionsObjectLibrary(childNode);

	let allChildrenSelected = parentNode?.allChildrenSelected;
	if (childNode.extendedData.objectType === ObjectTypes.hardwareControllers) {
		const isControllerFullSelected = controllerFullSelected?.find(item => item.ObjectId === childNode.extendedData.objectId)?.HasPermission;
		allChildrenSelected = isControllerFullSelected;
	}

	childNode.allChildrenSelected = allChildrenSelected;
	for (let i = 0; i < children.length; i++) {
		if (allChildrenSelected) {
			children[i].isChecked = allChildrenSelected;
		}
		buildObjectLibraryTree(children[i], childNode, controllerFullSelected);
	}
};

export const getSubFolder = (key: string, treeNode: ObjectLibraryTreeNode[]): ObjectLibraryTreeNode[] => {
	let children: ObjectLibraryTreeNode[] = [];
	treeNode.forEach(node => {
		if (node.key === key) {
			if (!node.children.length || node.extendedData.hasMockedData) {
				children.push(node);
			} else {
				children = node.children;
			}
		}
	});

	return [...children];
};

export const rebuildObjectLibraryTree = (
	updatedNode: ObjectLibraryTreeNode,
	childNode: ObjectLibraryTreeNode,
	searchType: SearchType = undefined,
	parentNode: ObjectLibraryTreeNode = undefined,
	updateChildOptions: boolean = false
): boolean => {
	let hasAllChildrenSelected = true;

	childNode.parentNode = parentNode;
	for (let i = 0; i < childNode.children.length; i++) {
		if (!searchType || (searchType && searchType.selectedHardwareOptions.some(item => childNode.children[i].extendedData.objectType === item))) {
			const isChecked = childNode.children[i].key === updatedNode.key ? updatedNode.isChecked : childNode.children[i].isChecked;
			if (!isChecked && childNode.children[i].folderType === ObjectLibraryFolder.ITEM) {
				hasAllChildrenSelected = false;
			}
		}

		if (updateChildOptions) {
			childNode.children[i].options = createOptionsObjectLibrary(childNode.children[i]);
		}

		if (childNode.children[i].key === updatedNode.key) {
			childNode.children[i] = updatedNode;
			if (!rebuildObjectLibraryTree(updatedNode, childNode.children[i], searchType, childNode, true)) {
				hasAllChildrenSelected = false;
			}
		} else {
			if (!rebuildObjectLibraryTree(updatedNode, childNode.children[i], searchType, childNode)) {
				hasAllChildrenSelected = false;
			}
		}
	}
	childNode.allChildrenSelected = hasAllChildrenSelected;

	return hasAllChildrenSelected;
};

export const getObjectKeyByValue = (obj, value) => {
	const indexValue = Object.values(obj).indexOf(value);
	return Object.keys(obj).at(indexValue);
};

export const clearSpaceObjectsAndRedLines = () => {};

const sortDirections: number[] = [GraphicSortDirections.None, GraphicSortDirections.Descend, GraphicSortDirections.Ascend];
export const getNextSortDirection = (value: number): number => {
	value++;
	let nextSort: number = GraphicSortDirections.Ascend;

	if (value !== 3) {
		for (let i: number = 0; i < sortDirections.length; i++) {
			if (sortDirections[i] >= value && sortDirections[i] < nextSort) {
				nextSort = sortDirections[i];
			}
		}
	} else {
		nextSort = GraphicSortDirections.None;
	}

	return nextSort;
};

export const allStatesImageDirectory: string = `${getFullVelocityUrl()}graphic/content/customInsertSpaceObjectMenu/images`;
export const getAllStatesImagePath = (objectType: ObjectTypes, singleState: boolean = false): string => {
	let imageName: string = '';

	switch (objectType) {
		case ObjectTypes.pollingPorts:
			imageName = 'Port';
			break;

		case ObjectTypes.xboxes:
			imageName = 'Xbox';
			break;

		case ObjectTypes.hardwareControllers:
			imageName = 'Controller';
			break;

		case ObjectTypes.doors:
			imageName = 'DoorAllStates';
			break;

		case ObjectTypes.inputs:
		case ObjectTypes.expansionInputs:
			imageName = 'Input';
			break;

		case ObjectTypes.relays:
		case ObjectTypes.expansionRelays:
			imageName = 'Relay';
			break;

		case ObjectTypes.readers:
			imageName = 'Reader';
			break;
		default:
			imageName = 'door_allstates01';
			break;
	}

	if (singleState) {
		imageName = `${imageName}_Default`;
	}

	return `${allStatesImageDirectory}/${imageName}.svg?_=${getScriptVersion()}`;
};

export const paintPMZ = (monitoringPoints: MonitoringPoint[], pointMonitoringZone: PointMonitoringZone, room: string): void => {
	const pmzState: ZoneState = getPMZState(monitoringPoints);
	const hightLightStyle: CSSProperties = {
		fillOpacity: 1,
		stroke: '#000000',
		strokeWidth: 3.0,
		strokeOpacity: 1,
		strokeLinejoin: 'round',
	};

	switch (pmzState) {
		case ZoneState.PMZ_ZONE_STATE_ALARM:
			hightLightStyle.fill = pointMonitoringZone.AlarmColor;
			break;
		case ZoneState.PMZ_ZONE_STATE_MASKED:
			hightLightStyle.fill = pointMonitoringZone.MaskedColor;
			break;
		case ZoneState.PMZ_ZONE_STATE_SECURE:
			hightLightStyle.fill = pointMonitoringZone.SecureColor;
			break;
		case ZoneState.PMZ_ZONE_STATE_UNKNOWN:
			hightLightStyle.fill = pointMonitoringZone.UnknownColor;
			break;
	}
};

const getPMZState = (monitoringPoints: MonitoringPoint[]): ZoneState => {
	let zoneState: ZoneState = ZoneState.PMZ_ZONE_STATE_SECURE;
	let atLeastOnePoint: boolean = false;

	for (const point of monitoringPoints?.filter(x => x.IsEnabled)) {
		atLeastOnePoint = true;

		switch (point.MaskState) {
			case PointState.PMZ_POINT_STATE_TRUE:
			case PointState.PMZ_POINT_STATE_ALARM:
				zoneState = ZoneState.PMZ_ZONE_STATE_MASKED;
				continue;

			case PointState.PMZ_POINT_STATE_UNKNOWN:
				if (zoneState !== ZoneState.PMZ_ZONE_STATE_MASKED) {
					zoneState = ZoneState.PMZ_ZONE_STATE_UNKNOWN;
				}
				break;
		}

		switch (point.Contact1Secured) {
			case PointState.PMZ_POINT_STATE_FALSE:
				zoneState = ZoneState.PMZ_ZONE_STATE_ALARM;
				continue;

			case PointState.PMZ_POINT_STATE_UNKNOWN:
				if (zoneState !== ZoneState.PMZ_ZONE_STATE_MASKED) {
					zoneState = ZoneState.PMZ_ZONE_STATE_UNKNOWN;
				}
				break;
		}

		switch (point.Contact3Secured) {
			case PointState.PMZ_POINT_STATE_TRUE:
			case PointState.PMZ_POINT_STATE_ALARM:
				zoneState = ZoneState.PMZ_ZONE_STATE_ALARM;
				continue;

			case PointState.PMZ_POINT_STATE_UNKNOWN:
				if (zoneState !== ZoneState.PMZ_ZONE_STATE_MASKED) {
					zoneState = ZoneState.PMZ_ZONE_STATE_UNKNOWN;
				}
				break;
		}
	}

	if (!atLeastOnePoint) {
		zoneState = ZoneState.PMZ_ZONE_STATE_UNKNOWN;
	}

	return zoneState;
};

export const handleIconStateChange = (specialEventType: SpecialEventType, uniqueId: string) => {
	switch (specialEventType) {
		case SpecialEventType.ContactOneClosed:
		case SpecialEventType.ContactTwoClosed:
		case SpecialEventType.ContactThreeClosed:
			break;

		case SpecialEventType.ContactOneOpen:
		case SpecialEventType.ContactTwoOpen:
		case SpecialEventType.ContactThreeOpen:
			break;

		case SpecialEventType.InputMasked:
			break;

		case SpecialEventType.InputUnmasked:
			break;

		case SpecialEventType.RelayActive:
			break;

		case SpecialEventType.RelayInactive:
			break;

		case SpecialEventType.AlarmActive:
			break;

		case SpecialEventType.AlarmAcknowledged:
			break;
		case SpecialEventType.ClearedAlarm:
			break;
	}
};

const draggableItems: ObjectTypes[] = [
	ObjectTypes.doors,
	ObjectTypes.expansionInputs,
	ObjectTypes.expansionRelays,
	ObjectTypes.hardwareControllers,
	ObjectTypes.inputs,
	ObjectTypes.pollingPorts,
	ObjectTypes.readers,
	ObjectTypes.relays,
	ObjectTypes.xboxes,
];

export const getDraggableItems = (): ObjectTypes[] => {
	return draggableItems;
};

export const loadDragImages = (): DictionaryCustom<ObjectTypes, HTMLImageElement>[] => {
	let images: DictionaryCustom<ObjectTypes, HTMLImageElement>[] = [];

	getDraggableItems().forEach((objectType: ObjectTypes) => {
		let img = new Image();
		img.src = getAllStatesImagePath(objectType, true);
		images.push({ Key: objectType, Value: img });
	});

	return images;
};

export const addDeviceSpaceObject = (
	device: Partial<VelocityObject>,
	coordinateX: number,
	coordinateY: number,
	mode: GraphicPageMode = GraphicPageMode.Design
) => {
	const { UniqueId, ObjectType, Name } = device;
	const objectTypeKey: string = getObjectKeyByValue(ObjectTypes, ObjectType);
	const imagePath: string = getAllStatesImagePath(ObjectType);

	graphicApi.getDeviceAlarmStatus(device.Address, device.ObjectType).then(triggeredClearedDeviceAlarmResponse => {
		if (!handleResponse(triggeredClearedDeviceAlarmResponse)) {
			const { HasAckAlarms, HasAlarms } = triggeredClearedDeviceAlarmResponse.Entity;
			device.HasAckAlarms = HasAckAlarms;
			device.HasAlarms = HasAlarms;
		}
	});
};

export const sendDeviceCommand = async (action: ActionDeviceTypes, spaceObject: Partial<VelocityObject>) => {
	const { ObjectId, ObjectType } = spaceObject;
	const isResetencry: boolean = action === ActionDeviceTypes.Resetencry;

	const deviceType = getDeviceObjectTypeBy(ObjectType);
	const { Entity: controllerContact } = await deviceAdminApi.getControllerContactsBySelectedKeys(deviceType, [ObjectId.toString()]);

	const deviceObject = controllerContact.at(0);

	const { HasResetEncryption, ControllerId, Id } = deviceObject;

	if (isResetencry) {
		if (HasResetEncryption) {
			WarningProtocolMessage({
				onConfirm: async () => await runDeviceCommand(action, ControllerId, Id, deviceType),
				onCancel: () => {},
				protocolData: { portId: Id, protocol: 0 },
			});
		}
	} else {
		await runDeviceCommand(action, ControllerId, Id, deviceType);
	}
};

const runDeviceCommand = async (action: ActionDeviceTypes, controllerId: number, deviceId: number, deviceType: DeviceObjectType) => {
	const command: SendDeviceCommand = {
		type: deviceType,
		action,
		controllerId,
		deviceId,
	};

	const response = await deviceAdminApi.sendDeviceCommand(command);
	handleResponse(response);
};

const isAcknowledgedAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_Acknowledge).allowed;
const isClearAlarmAllowed: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmsRTNs_ClearinStandardMode).allowed;

export const buildAlarmOptions = (): SelectOptions<ActionGraphic>[] => {
	const alarmOptions: SelectOptions<ActionGraphic>[] = [
		{
			id: 'alarmsActionButton',
			label: _('Action'),
			nestedOptions: [
				{
					id: 'acknowledgedAlarmsOption',
					label: _('AcknowledgedAlarmsContextMenu'),
					value: { action: ActionGraphicContextMenu.ACKNOWLEDGED_ALARMS },
					disabled: !isAcknowledgedAlarmAllowed || !mapsRightClickPermission,
					title: getPermissionErrorMessage(isAcknowledgedAlarmAllowed),
				},
				{
					id: 'clearAlarmsOption',
					label: _('ClearAlarmsContextMenu'),
					value: { action: ActionGraphicContextMenu.CLEAR_ALARMS },
					disabled: !isClearAlarmAllowed || !mapsRightClickPermission,
					title: getPermissionErrorMessage(isAcknowledgedAlarmAllowed),
				},
			],
		},
	];

	return alarmOptions;
};

type DigiTracActionControlOptionsProps = {
	deviceType: ObjectTypes;
	disabledOptions: DigitracDisabledOptions;
	availableOptions?: DigitracAvailableOptions;
	portResetEncryptionActionOptions?: PortResetEncryptionActionOptions;
	isMapsPage?: boolean;
};

const mapsRightClickPermission: boolean = User.getSubComponentPermission(user, SubPermissions.Maps_Maps_UseRight_ClickFunctions).allowed;
const portCanViewPermission: boolean = User.getComponentPermission(user, SecuredComponents.Ports).canView;

export const buildDigiTracActionControlOptions = ({
	deviceType,
	disabledOptions,
	availableOptions,
	portResetEncryptionActionOptions,
	isMapsPage,
}: DigiTracActionControlOptionsProps): SelectOptions<ActionDigitrac>[] => {
	const portComponentPermission: boolean = !isMapsPage
		? User.getComponentPermission(user, SecuredComponents.Ports).canUpdate
		: User.getComponentPermission(user, SecuredComponents.Ports).canUpdate && mapsRightClickPermission;
	const xboxComponentPermission: boolean = !isMapsPage
		? User.getComponentPermission(user, SecuredComponents.XBox).canUpdate
		: User.getComponentPermission(user, SecuredComponents.XBox).canUpdate && mapsRightClickPermission;
	const expansionInputComponentPermission: boolean = !isMapsPage
		? User.getComponentPermission(user, SecuredComponents.Expansion_Input).canUpdate
		: User.getComponentPermission(user, SecuredComponents.Expansion_Input).canUpdate && mapsRightClickPermission;
	const inputComponentPermission: boolean = !isMapsPage
		? User.getComponentPermission(user, SecuredComponents.Input).canUpdate
		: User.getComponentPermission(user, SecuredComponents.Input).canUpdate && mapsRightClickPermission;
	const controllerComponentPermission = !isMapsPage
		? User.getComponentPermission(user, SecuredComponents.Controller).canUpdate
		: User.getComponentPermission(user, SecuredComponents.Controller).canUpdate && mapsRightClickPermission;

	let options: SelectOptions<ActionDigitrac>[] = [];
	const { isControlDisabled } = disabledOptions;
	const { isActionOptionsAvailable } = availableOptions;

	const action = ActionDigitracContextMenu.SEND_COMMAND;

	switch (deviceType) {
		case ObjectTypes.pollingPorts:
			const { hasResetEncryption } = portResetEncryptionActionOptions;
			let opts = [
				{
					id: 'portEnablebutton',
					label: _('Enable'),
					value: { action, command: ActionDeviceTypes.Enabled },
					disabled: !portComponentPermission,
					title: getPermissionErrorMessage(portComponentPermission),
				},
				{
					id: 'portDisableButton',
					label: _('Disable'),
					value: { action, command: ActionDeviceTypes.Disabled },
					separator: hasResetEncryption,
					disabled: !portComponentPermission,
					title: getPermissionErrorMessage(portComponentPermission),
				},
			];

			if (hasResetEncryption) {
				const { hasMoreThanOne } = portResetEncryptionActionOptions;

				opts.push({
					id: 'portResetEncryptionButton',
					label: _('ResetEncryption'),
					value: { action, command: ActionDeviceTypes.Resetencry },
					disabled: !portComponentPermission || !portCanViewPermission || hasMoreThanOne,
					title: getPermissionErrorMessage(portComponentPermission && portCanViewPermission, false, hasMoreThanOne),
				});
			}

			options = [
				{
					id: 'portActionButton',
					label: _('Action'),
					nestedOptions: opts,
				},
			];
			break;

		case ObjectTypes.xboxes:
			options = [
				{
					id: 'xboxActionButton',
					label: _('Action'),
					nestedOptions: [
						{
							id: 'xboxEnableButton',
							label: _('Enable'),
							value: { action, command: ActionDeviceTypes.Enabled },
							disabled: !xboxComponentPermission,
							title: getPermissionErrorMessage(xboxComponentPermission),
						},
						{
							id: 'xboxDisableButton',
							label: _('Disable'),
							value: { action, command: ActionDeviceTypes.Disabled },
							disabled: !xboxComponentPermission,
							title: getPermissionErrorMessage(xboxComponentPermission),
						},
					],
				},
			];
			break;

		case ObjectTypes.hardwareControllers:
			options = [
				{
					id: 'controllerActionButton',
					label: _('Action'),
					nestedOptions: [
						{
							id: 'controllerEnableButton',
							label: _('Enabled'),
							value: { action, command: ActionDeviceTypes.Enabled },
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
						{
							id: 'controllerDisableButton',
							label: _('Disable'),
							value: { action, command: ActionDeviceTypes.Disabled },
							separator: true,
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
						{
							id: 'controllerDownloadConfigurationButton',
							label: _('DownloadConfiguration'),
							value: { action, command: ActionDeviceTypes.DownConfiguration },
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
						{
							id: 'controllerDownloadCredentialsButton',
							label: _('DownloadCredentials'),
							value: { action, command: ActionDeviceTypes.DownCredential },
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
						{
							id: 'controllerDownloadDateTimeButton',
							label: _('DownloadDate/Time'),
							value: { action, command: ActionDeviceTypes.DownDateTime },
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
						{
							id: 'controllerDownloadForgiveUsersButton',
							label: _('DownloadForgiveUsers'),
							value: { action, command: ActionDeviceTypes.DownForGiveUser },
							disabled: !controllerComponentPermission,
							title: getPermissionErrorMessage(controllerComponentPermission),
						},
					],
				},
			];
			break;

		case ObjectTypes.doors:
			options = [
				{
					id: 'doorAccessButton',
					label: _('Access'),
					disabled: isControlDisabled,
					title: getPermissionErrorMessage(!isControlDisabled),
					nestedOptions: [
						{
							id: 'doorMomentaryAccessButton',
							label: _('MomentaryAccess'),
							value: { action, command: ActionDeviceTypes.MomentaryAccess },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorUnlockButton',
							label: _('Unlock'),
							value: { action, command: ActionDeviceTypes.UnLockDoor },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorRelockButton',
							label: _('Relock'),
							value: { action, command: ActionDeviceTypes.RelockDoor },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorToggleLockButton',
							label: _('ToggleLock'),
							value: { action, command: ActionDeviceTypes.ToggleLock },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
					],
				},
				{
					id: 'doorControlButton',
					label: _('Control'),
					disabled: isControlDisabled,
					title: getPermissionErrorMessage(!isControlDisabled),
					nestedOptions: [
						{
							id: 'doorTriggerButton',
							label: _('Trigger'),
							value: { action, command: ActionDeviceTypes.Trigger },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorMaskButton',
							label: _('Mask'),
							value: { action, command: ActionDeviceTypes.Mask },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorUnMaskButton',
							label: _('UnMask'),
							value: { action, command: ActionDeviceTypes.UnMask },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorMomentaryMaskButton',
							label: _('MomentaryMask'),
							value: { action, command: ActionDeviceTypes.MomentaryMask },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorSuppressOperateButton',
							label: _('SuppressOperate'),
							value: { action, command: ActionDeviceTypes.SuppressOperate },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorSuppressOperateReleaseButton',
							label: _('SuppressOperateRelease'),
							value: { action, command: ActionDeviceTypes.SuppressOperateRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorForceOnButton',
							label: _('ForceOn'),
							value: { action, command: ActionDeviceTypes.ForceOn },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorForceOnReleaseButton',
							label: _('ForceOnRelease'),
							value: { action, command: ActionDeviceTypes.ForceOnRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorForceOffButton',
							label: _('ForceOff'),
							value: { action, command: ActionDeviceTypes.ForceOff },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorForceOffReleaseButton',
							label: _('ForceOffRelease'),
							value: { action, command: ActionDeviceTypes.ForceOffRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorLockDownButton',
							label: _('LockDown'),
							value: { action, command: ActionDeviceTypes.LockDown },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorLockDownReleaseButton',
							label: _('LockDownRelease'),
							value: { action, command: ActionDeviceTypes.LockDownRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorLockOpenButton',
							label: _('LockOpen'),
							value: { action, command: ActionDeviceTypes.LockOpen },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: 'doorLockOpenReleaseButton',
							label: _('LockOpenRelease'),
							value: { action, command: ActionDeviceTypes.LockOpenRelease },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
					],
				},
			];
			break;

		case ObjectTypes.inputs:
		case ObjectTypes.expansionInputs:
			const isExpansionInput = deviceType === ObjectTypes.expansionInputs;
			const isDisabled = isExpansionInput ? expansionInputComponentPermission : inputComponentPermission;

			const actionOptions: SelectOptions<ActionDigitrac>[] = [
				{
					id: isExpansionInput ? 'expansionInputActionButton' : 'inputActionButton',
					label: _('Action'),
					nestedOptions: [
						{
							id: isExpansionInput ? 'expansionInputEnableButton' : 'inputEnableButton',
							label: _('Enable'),
							value: { action, command: ActionDeviceTypes.Enabled },
							disabled: !isDisabled,
							title: getPermissionErrorMessage(isDisabled),
						},
						{
							id: isExpansionInput ? 'expansionInputDisableButton' : 'inputDisableButton',
							label: _('Disable'),
							value: { action, command: ActionDeviceTypes.Disabled },
							disabled: !isDisabled,
							title: getPermissionErrorMessage(isDisabled),
						},
					],
				},
			];

			const controlOptions: SelectOptions<ActionDigitrac>[] = [
				{
					id: isExpansionInput ? 'expansionInputControlButton' : 'inputControlButton',
					label: _('Control'),
					disabled: isControlDisabled,
					title: getPermissionErrorMessage(!isControlDisabled),
					nestedOptions: [
						{
							id: isExpansionInput ? 'expansionInputMaskButton' : 'inputMaskButton',
							label: _('Mask'),
							value: { action, command: ActionDeviceTypes.Mask },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionInput ? 'expansionInputUnMaskButton' : 'inputUnMaskButton',
							label: _('UnMask'),
							value: { action, command: ActionDeviceTypes.UnMask },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionInput ? 'expansionInputMomentaryMaskButton' : 'inputMomentaryMaskButton',
							label: _('MomentaryMask'),
							value: { action, command: ActionDeviceTypes.MomentaryMask },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
					],
				},
			];

			options = [...(isActionOptionsAvailable ? actionOptions : []), ...controlOptions];
			break;

		case ObjectTypes.relays:
		case ObjectTypes.expansionRelays:
			const isExpansionRelay = deviceType === ObjectTypes.expansionRelays;
			options = [
				{
					id: isExpansionRelay ? 'expansionRelayControlButton' : 'relayControlButton',
					label: _('Control'),
					disabled: isControlDisabled,
					title: getPermissionErrorMessage(!isControlDisabled),
					nestedOptions: [
						{
							id: isExpansionRelay ? 'expansionRelayTriggerButton' : 'relayTriggerButton',
							label: _('Trigger'),
							value: { action, command: ActionDeviceTypes.Trigger },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelaySuppressOperateButton' : 'relaySuppressOperateButton',
							label: _('SuppressOperate'),
							value: { action, command: ActionDeviceTypes.SuppressOperate },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelaySuppressOperateReleaseButton' : 'relaySuppressOperateReleaseButton',
							label: _('SuppressOperateRelease'),
							value: { action, command: ActionDeviceTypes.SuppressOperateRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayForceOnButton' : 'relayForceOnButton',
							label: _('ForceOn'),
							value: { action, command: ActionDeviceTypes.ForceOn },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayForceOnReleaseButton' : 'relayForceOnReleaseButton',
							label: _('ForceOnRelease'),
							value: { action, command: ActionDeviceTypes.ForceOnRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayForceOffButton' : 'relayForceOffButton',
							label: _('ForceOff'),
							value: { action, command: ActionDeviceTypes.ForceOff },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayForceOffReleaseButton' : 'relayForceOffReleaseButton',
							label: _('ForceOffRelease'),
							value: { action, command: ActionDeviceTypes.ForceOffRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayLockDownButton' : 'relayLockDownButton',
							label: _('LockDown'),
							value: { action, command: ActionDeviceTypes.LockDown },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayLockDownReleaseButton' : 'relayLockDownReleaseButton',
							label: _('LockDownRelease'),
							value: { action, command: ActionDeviceTypes.LockDownRelease },
							separator: true,
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayLockOpenButton' : 'relayLockOpenButton',
							label: _('LockOpen'),
							value: { action, command: ActionDeviceTypes.LockOpen },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
						{
							id: isExpansionRelay ? 'expansionRelayLockOpenReleaseButton' : 'relayLockOpenReleaseButton',
							label: _('LockOpenRelease'),
							value: { action, command: ActionDeviceTypes.LockOpenRelease },
							disabled: isControlDisabled,
							title: getPermissionErrorMessage(!isControlDisabled),
						},
					],
				},
			];
			break;
	}

	return options;
};

export const buildMonitorOptions = (): SelectOptions<ActionGraphic>[] => {
	const options: SelectOptions<ActionGraphic>[] = [
		{
			id: 'monitorDeleteAction',
			label: _('Delete'),
			disabled: false,
			value: { action: ActionGraphicContextMenu.DELETE },
			title: getPermissionErrorMessage(isAcknowledgedAlarmAllowed),
		},
		{
			id: 'monitorSetAsPrimaryAction',
			label: _('SetAsPrimary'),
			disabled: false,
			value: { action: ActionGraphicContextMenu.SET_AS_PRIMARY },
			title: getPermissionErrorMessage(isAcknowledgedAlarmAllowed),
		},
		{
			id: 'monitorConfigureAction',
			label: _('Configure'),
			disabled: false,
			value: { action: ActionGraphicContextMenu.CONFIGURE_DEVICE },
			title: getPermissionErrorMessage(isAcknowledgedAlarmAllowed),
		},
	];

	return options;
};

export const removeAlarms = (originalList: AlarmQueueTreeNode[], alarmsToRemove: AlarmModel[]): AlarmQueueTreeNode[] => {
	const alarmResults: AlarmQueueTreeNode[] = [];

	alarmsToRemove.forEach(a => {
		originalList.forEach(ca => {
			if (!isSameAlarmQueueKey(a.AlarmDetails[0].ActiveAlarmId, ca.key)) {
				alarmResults.push(ca);
			}
		});
	});

	return alarmResults;
};

export const updateTreeData = (list: AlarmQueueTreeNode[], key: React.Key, children: AlarmQueueTreeNode[]): AlarmQueueTreeNode[] => {
	const newList = list.map((node: AlarmQueueTreeNode) => {
		if (node.key === key) {
			return {
				...node,
				title: (
					<DeviceContent
						deviceInfo={{
							Address: node.Address,
							Count: children.length,
							Name: node.Name,
						}}
					/>
				),
				children,
			};
		}

		return node;
	});
	return newList;
};

export const setAlarmState = (device: Partial<VelocityObject>) => {
	if (device.HasAlarms) {
		handleIconStateChange(SpecialEventType.AlarmActive, device.UniqueId);
	} else {
		if (device.HasAckAlarms) {
			handleIconStateChange(SpecialEventType.AlarmAcknowledged, device.UniqueId);
		} else {
			handleIconStateChange(SpecialEventType.ClearedAlarm, device.UniqueId);
		}
	}
};

export const proccessLiveAlarmEvent = ({ alarmAddress, velocityObject, isVelocityObjectUpdated }: ProccessLiveAlarmEventParams) => {
	if (velocityObject) {
		if (isVelocityObjectUpdated) {
			setAlarmState(velocityObject);
		} else {
			graphicApi.getDeviceAlarmStatus(alarmAddress, velocityObject.ObjectType).then(triggeredClearedDeviceAlarmResponse => {
				if (!handleResponse(triggeredClearedDeviceAlarmResponse)) {
					const { HasAckAlarms, HasAlarms } = triggeredClearedDeviceAlarmResponse.Entity;
					velocityObject.HasAckAlarms = HasAckAlarms;
					velocityObject.HasAlarms = HasAlarms;
					setAlarmState(velocityObject);
				}
			});
		}
	}
};

export const getVelocityObjectType = (id: string): E_ObjectTypes => {
	const returnObject: E_ObjectTypes = E_ObjectTypes.OTYP_MDOBJECT;
	return returnObject;
};

// TODO: This is only a helper function, used to dev purposes, delete when needed
export const getCurrentVelocityObject = (
	id: string
): GraphicText | VelocityObject | PointMonitoringZone | GraphicCommandSet | GraphicLink | BadgeStudioImage | BadgeStudioBarCode => {
	let vo: VelocityObject = {
		Address: '\\\\PORT.001.0001',
		CanvasId: 1,
		CredentialDelay: 1,
		CredentialShow: false,
		ObjType: C_ObjectType.OT_TEXT,
		Data: '1:16',
		DefaultVO: false,
		HasAckAlarms: false,
		HasAlarms: false,
		Id: 1,
		InitialState: [],
		IsValidatedInDB: false,
		DisplayText: 'display text',
		Layer: -1,
		Name: 'Port 01',
		ObjectId: 1,
		ObjectType: ObjectTypes.pollingPorts,
		PositionInfo: {
			Id: 1,
			Bottom: 50,
			Left: 50,
			Right: 50,
			Top: 50,
			Rotation: 0,
		},
		UniqueId: 'UniqueId',
	};

	let base: ObjectBaseProperties = {
		ObjType: C_ObjectType.OT_TEXT,
		Id: 1,
		Name: 'Test name',
	};

	const text: GraphicText = {
		...base,
		DataField: '',
		UDF: 1,
		Id: 1,
		Name: '',
		ObjType: C_ObjectType.OT_TEXT,
		AppearanceInfo: {
			Id: 1,
			Visible: true,
			LineWidth: 1,
			LineColor: '#000000',
			Transparency: 0,
			BackColor: '#FFFFFF',
			BackStyle: 1,
			FillColor: '#FFFFFF',
			FillStyle: 1,
			LineStyle: 1,
		},
		FontInfo: {
			Id: 1,
			FontFamily: 'Arial',
			FontSize: 12,
			FontStyle: 1,
			TextColorHex: '#000000',
			AutoSize: false,
			DisplayText: 'Text display text',
			FontContainerStyle: 1,
		},
		PositionInfo: {
			Id: 1,
			Bottom: 0,
			Left: 0,
			Right: 0,
			Rotation: 0,
			Top: 0,
		},
	};

	return text;
};
